import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  AssignProject,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import { Role, User } from 'library/types';
import apiService from 'library/api-service';
import { useAppSelector } from 'store/hooks';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import SwipeUpIcon from '@mui/icons-material/SwipeUp';
import { FormattedMessage, useIntl } from 'react-intl';
import { CreateUserDialog, UpdateUserDialog } from './components';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';

const Users = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [openDeleteUser, setOpenDeleteUser] = useState(false);
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [openUpdateUser, setOpenUpdateUser] = useState(false);
  const [openAssignProject, setOpenAssignProject] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'nameSurname',
        headerName: intl.formatMessage({ id: 'users.nameSurname', defaultMessage: 'Adı / Soyadı' }),
        flex: 1,
      },
      {
        field: 'email',
        headerName: intl.formatMessage({ id: 'users.email', defaultMessage: 'E-Posta' }),
        flex: 1,
      },
      {
        field: 'otherEmail',
        headerName: intl.formatMessage({ id: 'users.otherEmail', defaultMessage: 'E-Posta ( Diğer )' }),
        flex: 1,
      },
      {
        field: 'roleId',
        headerName: intl.formatMessage({ id: 'users.role', defaultMessage: 'Rol' }),
        flex: 0.5,
        valueGetter: (value: number) => roles.find((role) => role.id === value)?.name || '',
      },
      {
        field: 'isActivityUser',
        headerName: intl.formatMessage({
          id: 'users.isActivityUser',
          defaultMessage: 'Aktivite Kullanıcısı',
        }),
        flex: 0.5,
        type: 'boolean',
      },
      {
        field: 'status',
        headerName: intl.formatMessage({
          id: 'users.status',
          defaultMessage: 'Durum',
        }),
        flex: 0.5,
        type: 'boolean',
        valueGetter: (value: number) => Boolean(value),
      },
    ],
    [intl, roles]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getUsers();

      if (response.isSuccess) {
        setData(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getRoles = async () => {
    try {
      setLoadingRoles(true);

      const response = await apiService.getRoles();

      if (response.isSuccess) {
        setRoles(response.data);
      }
    } catch {
    } finally {
      setLoadingRoles(false);
    }
  };

  // sync helper functions
  const handleCloseAssignProject = () => setOpenAssignProject(false);
  const handleClickOpenAssignProject = async () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // all-show-books control
    const selectedUser = data.find((user) => user.id === rowSelectionModel[0]) as User;

    if (selectedUser.roleId) {
      const {
        data: { permissionCodes },
      } = await apiService.getSpecificRole(selectedUser.roleId);

      if (permissionCodes.includes(STORAGE_KEY_ENUM.ALL_SHOW_BOOKS)) {
        enqueueSnackbar(
          <FormattedMessage
            id="users.allShowBooksControl"
            defaultMessage="Seçili kullanıcı sahip olduğu rol ile Tüm Projeler'e yetkilidir!"
          />,
          {
            variant: 'info',
          }
        );
        return;
      }
    }

    // If the check has been passed successfully...
    setOpenAssignProject(true);
  };

  const handleCloseDeleteUser = () => setOpenDeleteUser(false);
  const handleClickOpenDeleteUser = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteUser(true);
  };

  const handleCloseCreateUser = () => setOpenCreateUser(false);
  const handleClickOpenCreateUser = () => setOpenCreateUser(true);

  const handleCloseUpdateUser = () => setOpenUpdateUser(false);
  const handleClickOpenUpdateUser = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateUser(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-user')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    getValues();
  }, []);

  return (
    <>
      <Grid
        container
        justifyContent="flex-start"
        spacing={1}
        sx={{ width: { xs: '100vw', md: 'calc(100vw - 308px)' } }}>
        <PermissionHandler requiredPermissionCodes={['assign-project']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenAssignProject}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<SwipeUpIcon />}
              color="primary">
              <FormattedMessage id="users.assignProject" defaultMessage="Proje Ata" />
            </Button>
          </Grid>
          <Grid size={4}></Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['create-user']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateUser}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="users.createUser" defaultMessage="Kullanıcı Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-user']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateUser}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="users.updateUser" defaultMessage="Kullanıcı Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-user']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteUser}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="users.deleteUser" defaultMessage="Kullanıcı Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <AssignProject
        open={openAssignProject}
        handleClose={handleCloseAssignProject}
        userId={rowSelectionModel[0]}
        refresh={getValues}
      />
      <CreateUserDialog open={openCreateUser} handleClose={handleCloseCreateUser} roles={roles} refresh={getValues} />
      <UpdateUserDialog
        open={openUpdateUser}
        handleClose={handleCloseUpdateUser}
        userId={rowSelectionModel[0]}
        roles={roles}
        refresh={getValues}
      />
      <DeleteDialog
        open={openDeleteUser}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteUser}
        refresh={getValues}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="users.deleteUser" defaultMessage="Kullanıcı Sil" />}
        subTitle={
          <FormattedMessage
            id="users.deleteUserRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra kullanıcı veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteUser}
        successMessage={
          <FormattedMessage
            id="users.deleteUserCompleted"
            defaultMessage="Seçili kullanıcıyı silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading || loadingRoles}
          localeText={renderDataGridLocaleText()}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Users;
